import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useRoles } from "../../../core/api/useRoles";
import Table from "../../../core/common/dataTable";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { handleApiError } from "../../../core/errorHandle/handleHttpError";
import { Popover, Modal } from "antd";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";
import { usePermission } from "../../../core/api/useRolePermission";

const RoleSchema = Yup.object().shape({
  role_name: Yup.string().required("Role name is required"),
  status: Yup.string().required("Status is required"),
});

const RolesPermission = () => {
  const { roles, isLoading, addRole, updateRole, deleteRole } = useRoles();
  const [selectedRole, setSelectedRole] = useState<any | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const empToken = useAuthToken();

  // Permissions
  const canCreate = usePermission("Roles permissions", "create");
  const canUpdate = usePermission("Roles permissions", "write");
  const canDelete = usePermission("Roles permissions", "delete");
  const canRead   = usePermission("Roles permissions", "read");

  const handleAddSubmit = async (
    values: any,
    { setSubmitting, resetForm }: any
  ) => {
    try {
      await addRole(empToken, values);
      resetForm();
      document.getElementById("add_role_close")?.click();
    } catch (err: any) {
      handleApiError(err, "Failed to add role");
      document.getElementById("add_role_close")?.click();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      if (selectedRole?.role_name) {
        await updateRole(empToken, selectedRole.role_name, {
          status: values.status,
        });
        document.getElementById("edit_role_close")?.click();
      }
    } catch (err) {
      console.error("Edit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (selectedRole?.role_name) {
      await deleteRole(empToken, selectedRole.role_name);
    }
    setDeleteModalVisible(false);
  };

  const columns = [
    {
      title: "S.No",
      key: "serial",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Role",
      dataIndex: "role_name",
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge d-inline-flex align-items-center badge-xs ${
            text.toLowerCase() === "active" ? "badge-success" : "badge-danger"
          }`}
        >
          <i className="ti ti-point-filled me-1"></i>
          {text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()}
        </span>
      ),
    },
    {
      title: "Actions",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          <Popover content="Permission Matrix" placement="top">
            <Link
              to={all_routes.permissionpage}
              // state={{
              //   source: record,
              //   filterRole: record.role_name === "Admin" ? "Admin" : undefined,
              // }}
              className="me-2"
              onClick={(e) => {
                if (!canRead) {
                  toast.warning("You don't have permission to view the permission matrix.");
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
              }}
            >
              <i className="ti ti-shield" />
            </Link>
          </Popover>
          <button
            type="button"
            className="btn btn-outline-light p-1 me-2"
            onClick={(e) => {
              if (!canUpdate) {
                toast.warning("You don't have permission to edit roles.");
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setSelectedRole(record);
            }}
            data-bs-toggle={canUpdate ? "modal" : undefined}
            data-bs-target={canUpdate ? "#edit_role" : undefined}
          >
            <i className="ti ti-edit" />
          </button>
          <button
            type="button"
            className="btn btn-outline-light p-1"
            onClick={(e) => {
              if (!canDelete) {
                toast.warning("You don't have permission to delete roles.");
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setSelectedRole(record);
              setDeleteModalVisible(true);
            }}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Roles</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Roles</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => {
                  if (!canCreate) {
                    toast.warning("You don't have permission to create roles.");
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                }}
                data-bs-toggle={canCreate ? "modal" : undefined}
                data-bs-target={canCreate ? "#add_role" : undefined}
              >
                <i className="ti ti-circle-plus me-2" /> Add New Role
              </button>
            </div>
            <div className="ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            {isLoading ? (
              <p className="p-3">Loading...</p>
            ) : (
              <Table
                dataSource={
                  Array.isArray(roles)
                    ? roles.map((role: any) => ({
                        ...role,
                        key: role.role_name,
                      }))
                    : []
                }
                columns={columns}
              />
            )}
          </div>
        </div>
      </div>
      <div className="modal fade" id="add_role">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Role</h4>
              <button
                type="button"
                className="btn-close"
                id="add_role_close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <Formik
              initialValues={{ role_name: "", status: "Active" }}
              validationSchema={RoleSchema}
              onSubmit={handleAddSubmit}
            >
              {({ isSubmitting, errors, touched, resetForm }) => (
                <Form>
                  <div className="modal-body pb-0">
                    <div className="mb-3">
                      <label className="form-label">Role Name</label>
                      <Field name="role_name" className="form-control" />
                      {errors.role_name && touched.role_name && (
                        <div className="text-danger">{errors.role_name}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <Field as="select" name="status" className="form-control">
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </Field>
                      {errors.status && touched.status && (
                        <div className="text-danger">{errors.status}</div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-white me-2"
                      onClick={() => resetForm()}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      Add Role
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <div className="modal fade" id="edit_role">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Role</h4>
              <button
                type="button"
                className="btn-close"
                id="edit_role_close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <Formik
              enableReinitialize
              initialValues={{
                role_name: selectedRole?.role_name || "",
                status: selectedRole?.status || "Active",
              }}
              validationSchema={RoleSchema}
              onSubmit={handleEditSubmit}
            >
              {({ isSubmitting, errors, touched, resetForm }) => (
                <Form>
                  <div className="modal-body pb-0">
                    <div className="mb-3">
                      <label className="form-label">Role Name</label>
                      <Field
                        name="role_name"
                        className="form-control"
                        disabled
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <Field as="select" name="status" className="form-control">
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </Field>
                      {errors.status && touched.status && (
                        <div className="text-danger">
                          {errors.status as String}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-white me-2"
                      onClick={() => resetForm()}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{selectedRole?.role_name}</strong>?
        </p>
      </Modal>
    </div>
  );
};
export default RolesPermission;